
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { fetchPublishedArticles, Article } from '../../../utils/articleApi';
import ArticleCategoryBadges from '../../../components/feature/ArticleCategoryBadges';
import AuthorAvatar from '../../../components/feature/AuthorAvatar';

const DEFAULT_MAX = 7;

export type LatestArticlesCta = {
  to: string;
  label: string;
};

export type LatestArticlesProps = {
  /** 表示件数（デフォルト 7） */
  maxItems?: number;
  /**
   * セクション末尾の主ボタン。
   * 未指定＝コラム一覧ページ用（#categories へジャンプ）
   */
  cta?: LatestArticlesCta;
};

export default function LatestArticles({
  maxItems = DEFAULT_MAX,
  cta,
}: LatestArticlesProps = {}) {
  const [publishedArticles, setPublishedArticles] = useState<Article[]>([]);
  const location = useLocation();
  const navigate = useNavigate();
  const resolvedCta: LatestArticlesCta = cta ?? {
    to: '#categories',
    label: 'カテゴリから探す',
  };
  const ctaIcon =
    resolvedCta.to.startsWith('#') ? 'ri-arrow-down-line' : 'ri-arrow-right-line';

  const scrollToHashId = (hash: string) => {
    const id = hash.startsWith('#') ? hash.slice(1) : hash;
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.history.replaceState(
      null,
      '',
      `${location.pathname}${location.search}${hash.startsWith('#') ? hash : `#${hash}`}`
    );
  };

  const onCtaClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!resolvedCta.to.startsWith('#')) return;
    e.preventDefault();
    const id = resolvedCta.to.slice(1);
    const el = document.getElementById(id);
    if (el) {
      scrollToHashId(resolvedCta.to);
      return;
    }
    navigate(`/articles${resolvedCta.to}`);
  };

  useEffect(() => {
    const loadArticles = async () => {
      const articles = await fetchPublishedArticles();
      const published = articles.filter((article) => article.status === '公開');
      const sorted = published
        .sort((a, b) => {
          const dateA = new Date(a.publishedAt || a.publishDate || '');
          const dateB = new Date(b.publishedAt || b.publishDate || '');
          return dateB.getTime() - dateA.getTime();
        })
        .slice(0, maxItems);
      setPublishedArticles(sorted);
    };
    loadArticles();
  }, [maxItems]);

  return (
    <section className="py-8 sm:py-12 md:py-16 bg-[#F8FAFC]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 sm:mb-10 md:mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#111827] mb-3 sm:mb-4">
            最新旅行記事
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-[#6B7280] max-w-2xl mx-auto px-4">
            旅行に関する最新情報をお届けします
          </p>
        </div>

        {/* Articles Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
          {publishedArticles.map((article) => (
            <Link 
              key={article.id}
              to={`/articles/${article.id}`}
              className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300"
            >
              <div className="aspect-video overflow-hidden">
                <img 
                  src={article.image}
                  alt={article.title}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              
              <div className="p-4 sm:p-6">
                <div className="mb-2 sm:mb-3">
                  <ArticleCategoryBadges article={article} />
                </div>
                
                <h3 className="text-lg sm:text-xl font-bold text-[#111827] mb-2 sm:mb-3 group-hover:text-[#14B8A6] transition-colors line-clamp-2">
                  {article.title}
                </h3>
                
                <p className="text-sm sm:text-base text-[#6B7280] mb-3 sm:mb-4 line-clamp-3">
                  {article.excerpt}
                </p>
                
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 text-xs sm:text-sm text-[#6B7280]">
                  <div className="flex items-center space-x-2">
                    <AuthorAvatar size="responsive" />
                    <span className="truncate">{article.author}</span>
                  </div>
                  <div className="flex items-center space-x-2 sm:space-x-4">
                    <span className="flex items-center space-x-1">
                      <i className="ri-time-line"></i>
                      <span className="whitespace-nowrap">{article.readTime}</span>
                    </span>
                    <span className="whitespace-nowrap">{article.publishDate}</span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="text-center mt-8 sm:mt-10 md:mt-12">
          {resolvedCta.to.startsWith('#') ? (
            <a
              href={`${location.pathname}${location.search}${resolvedCta.to}`}
              onClick={onCtaClick}
              className="inline-flex items-center space-x-2 bg-[#14B8A6] text-white px-6 sm:px-8 py-2 sm:py-3 rounded-lg hover:bg-[#0F9488] transition-colors font-medium text-sm sm:text-base cursor-pointer"
            >
              <span>{resolvedCta.label}</span>
              <i className={ctaIcon}></i>
            </a>
          ) : (
            <Link
              to={resolvedCta.to}
              className="inline-flex items-center space-x-2 bg-[#14B8A6] text-white px-6 sm:px-8 py-2 sm:py-3 rounded-lg hover:bg-[#0F9488] transition-colors font-medium text-sm sm:text-base"
            >
              <span>{resolvedCta.label}</span>
              <i className={ctaIcon}></i>
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}